import React, { createContext, useContext, useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import useTabOrderLock from "../hooks/useTabOrderLock";
import { getRevisionIdFromPath, isDefinitelyNonRevisionPath } from "../util/revisionLockUtils";

const RevisionLockContext = createContext(null);

/**
 * Fallback when revision id cannot be resolved (should be rare).
 * Lock keys are normally per-revision (`revision_edit_<id>`) so different revisions in different tabs
 * do not compete for one global Web Lock — otherwise only one tab in the browser could edit any revision.
 * Within one revision, the id stays stable across Document <-> view-section / view-step navigation.
 */
const REVISION_LOCK_KEY = "revision_edit";

/** Primary tab = first to acquire; we yield to it on refresh. Never overwrite until holder has held 8s (prev owner gone). */
const RETURNING_OWNER_KEY = "revision_returning_owner";
const PRIMARY_TAB_KEY = "revision_primary_tab";
const RETURNING_OWNER_GRACE_MS = 6000;
const YIELD_CHECK_INTERVAL_MS = 400;
const CONTENDER_SIGNAL_INTERVAL_MS = 400;
const PRIMARY_OVERWRITE_DELAY_MS = 8000; // Only overwrite primary after holding this long (prev owner closed)

/**
 * Provider that holds the Web Locks API lock while the user is on any revision page.
 * Lock name is scoped by revision id so the same browser can edit different revisions in different tabs;
 * within one revision, the id stays stable across navigation (Document, view-revision, view-section, etc.).
 * Only release when the user leaves revision pages (e.g. /login, /).
 * When the owner tab refreshes, the other tab yields so the refreshed tab reclaims the lock.
 */
export function RevisionLockProvider({ children }) {
  const location = useLocation();
  const pathname = location?.pathname ?? "";
  const revisionIdFromPath = getRevisionIdFromPath(pathname);
  const lastRevisionIdRef = useRef(null);
  const [isYielding, setIsYielding] = useState(false);

  // Sticky: once we've seen a revision id, keep considering ourselves "on revision" until we're on a definite non-revision path
  if (revisionIdFromPath != null) {
    lastRevisionIdRef.current = revisionIdFromPath;
  } else if (isDefinitelyNonRevisionPath(pathname)) {
    lastRevisionIdRef.current = null;
  }

  const isOnRevisionPage = revisionIdFromPath != null || lastRevisionIdRef.current != null;

  const revisionId = revisionIdFromPath ?? lastRevisionIdRef.current ?? null;

  const routeKeyForLock =
    isOnRevisionPage && !isYielding
      ? revisionId != null
        ? `${REVISION_LOCK_KEY}_${revisionId}`
        : REVISION_LOCK_KEY
      : "revision_none";

  const { isFirstTab, isLocked } = useTabOrderLock(routeKeyForLock);

  // Locked tab only: write "I want to reclaim" so holder can yield. Holder must never write.
  useEffect(() => {
    if (!isOnRevisionPage || isFirstTab || typeof localStorage === "undefined") return;
    const tabId = sessionStorage.getItem("tab-id");
    if (!tabId) return;
    const write = () => {
      try {
        localStorage.setItem(
          RETURNING_OWNER_KEY,
          JSON.stringify({ tabId, timestamp: Date.now() })
        );
      } catch (_) {}
    };
    write();
    const id = setInterval(write, CONTENDER_SIGNAL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOnRevisionPage, isFirstTab]);

  // Holder only: set primary if empty; overwrite only after holding 8s; yield when primary (returning) wants lock
  useEffect(() => {
    if (!isFirstTab || !isOnRevisionPage || isYielding) return;
    const myTabId = sessionStorage.getItem("tab-id");
    if (!myTabId) return;

    try {
      const currentPrimary = localStorage.getItem(PRIMARY_TAB_KEY);
      if (!currentPrimary) localStorage.setItem(PRIMARY_TAB_KEY, myTabId);
    } catch (_) {}
    const overwriteTimer = setTimeout(() => {
      try {
        localStorage.setItem(PRIMARY_TAB_KEY, myTabId);
      } catch (_) {}
    }, PRIMARY_OVERWRITE_DELAY_MS);

    const check = () => {
      try {
        const primary = localStorage.getItem(PRIMARY_TAB_KEY);
        const raw = localStorage.getItem(RETURNING_OWNER_KEY);
        if (!raw || !primary) return;
        const data = JSON.parse(raw);
        const { tabId: contenderTabId, timestamp } = data || {};
        if (contenderTabId === myTabId) return;
        if (contenderTabId !== primary) return; // only yield to primary tab (first owner, returning after refresh)
        if (!timestamp || Date.now() - timestamp > RETURNING_OWNER_GRACE_MS) return;
        setIsYielding(true);
        setTimeout(() => setIsYielding(false), 4000);
      } catch (_) {}
    };

    check();
    const id = setInterval(check, YIELD_CHECK_INTERVAL_MS);
    return () => {
      clearTimeout(overwriteTimer);
      clearInterval(id);
    };
  }, [isFirstTab, isOnRevisionPage, isYielding]);

  const effectiveIsFirstTab = isFirstTab && !isYielding;
  const effectiveIsLocked = isLocked || isYielding;
  const routeKey = routeKeyForLock;

  const value = React.useMemo(
    () => ({
      revisionIdFromPath: revisionId,
      routeKey,
      isFirstTab: effectiveIsFirstTab,
      isLocked: effectiveIsLocked,
    }),
    [revisionId, routeKey, effectiveIsFirstTab, effectiveIsLocked]
  );

  return (
    <RevisionLockContext.Provider value={value}>
      {children}
    </RevisionLockContext.Provider>
  );
}

export function useRevisionLock() {
  const ctx = useContext(RevisionLockContext);
  return ctx;
}
